import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import AppRoutes from './routes';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, ICurrentUser } from './contexts/AuthContext';
import { queryClient } from './lib/queryClient';
import { api } from './services/client';

const App = () => {
  const [authToken, setAuthToken] = useState<string | null>(
    localStorage.getItem('adminToken'),
  );
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (authToken) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [authToken]);

  const verifyToken = async () => {
    try {
      const user = await api.get<ICurrentUser>('/backoffice/auth/me');
      setCurrentUser(user);
    } catch {
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token: string, user: any) => {
    localStorage.setItem('adminToken', token);
    setAuthToken(token);
    setCurrentUser(user);
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthToken(null);
    setCurrentUser(null);
    queryClient.clear();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading...
      </div>
    );
  }

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider currentUser={currentUser}>
          <AppRoutes
            currentUser={currentUser}
            onLogout={handleLogout}
            onUserUpdated={setCurrentUser}
          />
          <Toaster />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
